// Light/dark switch of the public website (React SiteHeader/FlowTop and the
// ERB header of the text pages). Same storage as the app's theme menu
// (lib/theme.ts): the browser keeps `mektebi.appearance`, and a signed-in
// person's choice is saved to their account (user.theme), which the site
// layouts read first — so the site always follows what they chose in the app.
import { APPEARANCE_KEY, persistAppearance } from "@/lib/theme"

// The visitor's current choice ("light" | "dark" | "system"); null until known.
let choice: string | null = null

// The phone's status/toolbar colour follows the page, not only the OS.
function syncThemeColor(dark: boolean) {
  document.querySelectorAll('meta[name="theme-color"]').forEach((meta) => meta.setAttribute("content", dark ? "#131513" : "#faf9f6"))
}

export function siteIsDark() {
  return document.documentElement.classList.contains("dark")
}

export function setSiteAppearance(next: "light" | "dark", signedIn: boolean) {
  const root = document.documentElement
  // Colours switch at once: no half-faded buttons and cards.
  root.classList.add("mk-theme-switching")
  root.classList.toggle("dark", next === "dark")
  root.style.colorScheme = next
  syncThemeColor(next === "dark")
  requestAnimationFrame(() => requestAnimationFrame(() => root.classList.remove("mk-theme-switching")))

  try {
    localStorage.setItem(APPEARANCE_KEY, next)
  } catch {
    // private mode: the switch still works for this page
  }
  choice = next
  if (signedIn) persistAppearance(next)
  document.dispatchEvent(new CustomEvent("mektebi:appearance", { detail: next }))
}

// While the choice is "system", follow the OS when it changes.
export function followSystemAppearance(appearance: string | null) {
  const media = window.matchMedia("(prefers-color-scheme: dark)")
  if (choice == null) {
    // Signed in: the account's choice (from the server). Signed out: the browser's.
    choice = appearance
    if (choice == null) {
      try {
        choice = localStorage.getItem(APPEARANCE_KEY)
      } catch {
        choice = null
      }
    }
  }
  const onChange = () => {
    if (choice && choice !== "system") return
    document.documentElement.classList.toggle("dark", media.matches)
    document.documentElement.style.colorScheme = media.matches ? "dark" : "light"
    syncThemeColor(media.matches)
    document.dispatchEvent(new CustomEvent("mektebi:appearance", { detail: media.matches ? "dark" : "light" }))
  }
  media.addEventListener("change", onChange)
  return () => media.removeEventListener("change", onChange)
}

// ERB pages: every [data-theme-toggle] button and [data-theme-switch] checkbox
// (data-signed-in="true" when signed in, data-appearance = the account's choice).
export function initThemeToggles() {
  const buttons = document.querySelectorAll<HTMLButtonElement>("[data-theme-toggle]")
  const switches = document.querySelectorAll<HTMLInputElement>("[data-theme-switch]")
  const sync = () => {
    buttons.forEach((button) => button.setAttribute("aria-pressed", String(siteIsDark())))
    switches.forEach((input) => (input.checked = siteIsDark()))
  }
  buttons.forEach((button) => {
    button.addEventListener("click", () => setSiteAppearance(siteIsDark() ? "light" : "dark", button.dataset.signedIn === "true"))
  })
  switches.forEach((input) => {
    input.addEventListener("change", () => setSiteAppearance(input.checked ? "dark" : "light", input.dataset.signedIn === "true"))
  })
  document.addEventListener("mektebi:appearance", sync)
  sync()
  const first = buttons[0] ?? switches[0]
  followSystemAppearance(first?.dataset.appearance || null)
}
